/**
 * /llm-proxy — World-class drop-in OpenAI / Anthropic / Gemini / Groq / Mistral proxy with observability.
 * ============================================================================
 * Three tabs:
 *   1. Setup — copy-paste snippets (5 languages), proxy key management
 *   2. Logs — table of recent proxied requests with cost + latency
 *   3. Stats — per-model breakdown, p50/p95, error rate
 *
 * Brand: slate-950, emerald accents.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from '../components/ui/dialog';
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from '../components/ui/tabs';
import {
  Network, Plus, ArrowLeft, Copy, Check, Trash2, KeyRound, AlertCircle,
  Activity, DollarSign, Clock, Zap, Cpu, Sparkles, Database, Scissors, GitBranch,
} from 'lucide-react';
import { toast } from 'sonner';
import BrandTitle from '../components/BrandTitle';
import LiveCostMeter from '../components/LiveCostMeter';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
const fmtCost = (n) => n >= 0.01 ? `$${n.toFixed(4)}` : `$${(n || 0).toFixed(6)}`;
const fmtDate = (s) => { try { return new Date(s).toLocaleString(); } catch { return s; } };

const SNIPPET_LANGS = [
  { id: 'python', label: 'Python (OpenAI SDK)' },
  { id: 'javascript', label: 'Node.js (OpenAI)' },
  { id: 'typescript', label: 'TypeScript (OpenAI)' },
  { id: 'python-anthropic', label: 'Python (Anthropic)' },
  { id: 'node-anthropic', label: 'Node.js (Anthropic)' },
  { id: 'curl', label: 'cURL' },
];

const LlmProxy = () => {
  const navigate = useNavigate();
  const [keys, setKeys] = useState([]);
  const [requests, setRequests] = useState([]);
  const [stats, setStats] = useState(null);
  const [savings, setSavings] = useState(null);
  const [streamStats, setStreamStats] = useState(null);
  const [embeddingStats, setEmbeddingStats] = useState(null);
  const [loading, setLoading] = useState(true);

  // create-key flow
  const [createOpen, setCreateOpen] = useState(false);
  const [newKeyName, setNewKeyName] = useState('default');
  const [revealedKey, setRevealedKey] = useState(null);
  const [copiedSecret, setCopiedSecret] = useState(false);

  // setup snippet tab
  const [snippetLang, setSnippetLang] = useState('python');
  const [snippetCode, setSnippetCode] = useState('');
  const [copiedSnippet, setCopiedSnippet] = useState(false);

  // request detail
  const [reqDetail, setReqDetail] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [k, r, s, sv, ss, es] = await Promise.all([
        axios.get(`${API}/llm-proxy/keys`, auth()),
        axios.get(`${API}/llm-proxy/requests?limit=50`, auth()),
        axios.get(`${API}/llm-proxy/stats`, auth()),
        axios.get(`${API}/llm-proxy/savings`, auth()),
        axios.get(`${API}/llm-proxy/stream-cache-stats`, auth()).catch(() => ({ data: null })),
        axios.get(`${API}/llm-proxy/embedding-cache-stats`, auth()).catch(() => ({ data: null })),
      ]);
      setKeys(k.data.items || []);
      setRequests(r.data.items || []);
      setStats(s.data);
      setSavings(sv.data);
      setStreamStats(ss.data);
      setEmbeddingStats(es.data);
    } catch (e) {
      if (e?.response?.status === 401 || e?.response?.status === 403) navigate('/login');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  useEffect(() => {
    axios.get(`${API}/llm-proxy/setup?language=${snippetLang}`)
      .then(({ data }) => setSnippetCode(data.code || ''))
      .catch(() => setSnippetCode('# (failed to load snippet)'));
  }, [snippetLang]);

  const onCreateKey = async () => {
    try {
      const { data } = await axios.post(`${API}/llm-proxy/keys`, { name: newKeyName || 'default' }, auth());
      setRevealedKey(data);
      setCreateOpen(false);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Create failed'); }
  };

  const onRevoke = async (id) => {
    if (!window.confirm('Revoke this key? Existing clients using it will start failing.')) return;
    await axios.delete(`${API}/llm-proxy/keys/${id}`, auth()).catch(() => {});
    toast.success('Key revoked');
    load();
  };

  const copy = (text, setter) => {
    navigator.clipboard.writeText(text || '');
    setter(true);
    setTimeout(() => setter(false), 1500);
  };

  return (
    <div className="min-h-screen bg-white text-slate-900" data-testid="llm-proxy-page">
      {/* v35.36 — SEOHead removed: /llm-proxy is an authenticated dashboard that
          redirects to /login. It is disallowed in robots.txt and out of the
          sitemap so crawlers never index an auth wall. */}
      <nav className="sticky top-0 z-50 backdrop-blur-md bg-white/80 border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-6 h-14 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-2 text-slate-600 hover:text-slate-900"><ArrowLeft className="w-4 h-4" /> Dashboard</Link>
          <div className="flex items-center gap-3 text-sm">
            <Link to="/prompt-playground" className="text-slate-600 hover:text-slate-900">Playground</Link>
            <Link to="/prompts" className="text-slate-600 hover:text-slate-900">Prompts</Link>
            <Link to="/traces" className="text-slate-600 hover:text-slate-900">Traces</Link>
            <Link to="/evals" className="text-slate-600 hover:text-slate-900">Evals</Link>
          </div>
        </div>
      </nav>

      <main className="max-w-6xl mx-auto px-6 py-10">
        <Badge className="bg-emerald-500/15 border-emerald-500/30 text-emerald-300 px-3 py-1 text-[11px] tracking-[0.15em] uppercase font-semibold mb-3">
          <Network className="w-3 h-3 mr-1.5 inline" /> LLM proxy
        </Badge>
        <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight mb-1" data-testid="llm-proxy-h1"><BrandTitle>LLM Proxy</BrandTitle></h1>
        <p className="text-slate-400 mb-6">Change <span className="text-emerald-300 font-mono">base_url</span> in one line. Get observability + cost tracking on every LLM call. Your provider keys are <span className="text-emerald-300">never stored</span>.</p>

        {/* v34.4 (Mar 2026): always-visible live cost console — like `tail -f` for the AI bill */}
        <div className="mb-8">
          <LiveCostMeter />
        </div>

        <Tabs defaultValue="setup">
          <TabsList className="bg-slate-900 border border-slate-800" data-testid="proxy-tabs">
            <TabsTrigger value="setup" data-testid="proxy-tab-setup">Setup</TabsTrigger>
            <TabsTrigger value="savings" data-testid="proxy-tab-savings">Auto-Compiler Savings</TabsTrigger>
            <TabsTrigger value="logs" data-testid="proxy-tab-logs">Logs ({requests.length})</TabsTrigger>
            <TabsTrigger value="stats" data-testid="proxy-tab-stats">Stats</TabsTrigger>
          </TabsList>

          {/* SETUP */}
          <TabsContent value="setup" className="mt-6 space-y-6">
            {/* Proxy keys */}
            <Card className="bg-white border-slate-200 shadow-sm">
              <CardContent className="p-5">
                <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                  <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2"><KeyRound className="w-4 h-4 text-emerald-400" /> Proxy keys</h2>
                  <Dialog open={createOpen} onOpenChange={setCreateOpen}>
                    <DialogTrigger asChild>
                      <Button className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold" data-testid="proxy-create-key-btn"><Plus className="w-4 h-4 mr-2" /> New proxy key</Button>
                    </DialogTrigger>
                    <DialogContent className="bg-slate-900 border-slate-800 text-slate-100">
                      <DialogHeader><DialogTitle>Create proxy key</DialogTitle></DialogHeader>
                      <Input placeholder="Key name (e.g. production, dev)" value={newKeyName} onChange={(e) => setNewKeyName(e.target.value)} className="bg-white border-slate-300" data-testid="proxy-new-key-name" />
                      <DialogFooter>
                        <Button variant="ghost" onClick={() => setCreateOpen(false)}>Cancel</Button>
                        <Button onClick={onCreateKey} className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold" data-testid="proxy-create-submit">Create</Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                </div>

                {/* Revealed-key one-time display */}
                {revealedKey && (
                  <Card className="bg-amber-500/10 border-amber-500/40 mb-3" data-testid="proxy-revealed-key">
                    <CardContent className="p-4">
                      <div className="flex items-start gap-2 text-amber-300 text-xs uppercase tracking-wider font-bold mb-2"><AlertCircle className="w-4 h-4 shrink-0" /> {revealedKey.warning}</div>
                      <div className="flex items-center gap-2 bg-slate-950 text-slate-100 border border-amber-500/30 rounded p-2">
                        <code className="text-sm text-emerald-300 font-mono flex-1 break-all" data-testid="proxy-secret-display">{revealedKey.secret_key}</code>
                        <Button size="sm" variant="ghost" onClick={() => copy(revealedKey.secret_key, setCopiedSecret)} data-testid="proxy-copy-secret">
                          {copiedSecret ? <><Check className="w-3.5 h-3.5 mr-1 text-emerald-400" /> Copied</> : <><Copy className="w-3.5 h-3.5 mr-1" /> Copy</>}
                        </Button>
                      </div>
                      <Button variant="ghost" className="text-xs text-slate-400 mt-2" onClick={() => setRevealedKey(null)}>Dismiss</Button>
                    </CardContent>
                  </Card>
                )}

                {loading ? <p className="text-slate-500 text-sm">Loading…</p> :
                  keys.length === 0 ? (
                    <p className="text-slate-500 text-sm">No keys yet. Create one above to get started.</p>
                  ) : (
                    <div className="space-y-1.5">
                      {keys.map((k) => (
                        <div key={k.key_id} className="flex items-center justify-between p-3 bg-slate-950 text-slate-100 border border-emerald-500/20 hover:border-emerald-500/40 rounded transition-colors" data-testid={`proxy-key-row-${k.key_id}`}>
                          <div className="flex items-center gap-3 min-w-0 flex-1">
                            <code className="text-xs text-emerald-300 font-mono font-bold">{k.key_prefix}</code>
                            <span className="text-sm text-slate-100 truncate font-medium">{k.name}</span>
                            <Badge className="bg-sky-500/15 border border-sky-500/40 text-sky-200 px-1.5 py-0 text-[9px] shrink-0 font-bold">{k.request_count || 0} req</Badge>
                            <Badge className="bg-emerald-500/15 border border-emerald-500/40 text-emerald-200 px-1.5 py-0 text-[9px] shrink-0 font-bold">{fmtCost(k.total_cost_usd || 0)}</Badge>
                          </div>
                          <div className="flex items-center gap-2 text-[10px] text-slate-300">
                            <span className="font-mono">{fmtDate(k.created_at)}</span>
                            <button onClick={() => onRevoke(k.key_id)} className="text-slate-400 hover:text-rose-400 ml-2 transition-colors" data-testid={`proxy-key-revoke-${k.key_id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )
                }
              </CardContent>
            </Card>

            {/* Setup snippet */}
            <Card className="bg-white border-slate-200 shadow-sm">
              <CardContent className="p-5">
                <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                  <h2 className="text-lg font-bold text-slate-900">Drop-in snippet</h2>
                  <div className="flex items-center gap-2">
                    {SNIPPET_LANGS.map((l) => (
                      <Button key={l.id} size="sm" variant={snippetLang === l.id ? 'default' : 'outline'}
                        className={snippetLang === l.id ? 'bg-emerald-500 text-slate-950 hover:bg-emerald-400' : 'border-slate-700 text-slate-300 hover:bg-slate-800'}
                        onClick={() => setSnippetLang(l.id)} data-testid={`proxy-snippet-${l.id}`}>{l.label}</Button>
                    ))}
                  </div>
                </div>
                <div className="relative">
                  <Button size="sm" variant="ghost" className="absolute right-2 top-2 z-10 text-slate-600" onClick={() => copy(snippetCode, setCopiedSnippet)} data-testid="proxy-copy-snippet">
                    {copiedSnippet ? <><Check className="w-3.5 h-3.5 mr-1 text-emerald-400" /> Copied</> : <><Copy className="w-3.5 h-3.5 mr-1" /> Copy</>}
                  </Button>
                  <pre className="text-xs bg-slate-950 p-4 rounded border border-slate-800 overflow-auto max-h-[60vh] whitespace-pre-wrap text-emerald-100 font-mono">{snippetCode}</pre>
                </div>
              </CardContent>
            </Card>

            {/* Trust callout */}
            <Card className="bg-emerald-500/5 border-emerald-500/30">
              <CardContent className="p-5">
                <h3 className="text-sm font-bold text-emerald-300 mb-2">🔒 Your provider keys are never stored</h3>
                <p className="text-xs text-slate-600 leading-relaxed">
                  We forward your <span className="font-mono">Authorization: Bearer sk-…</span> header verbatim to the upstream provider (OpenAI / Anthropic) and discard it once the response returns. Only a SHA-256 hash of YOUR Cartie proxy key is persisted, so a database breach reveals zero secrets.
                </p>
              </CardContent>
            </Card>
          </TabsContent>

          {/* LOGS */}
          <TabsContent value="logs" className="mt-6">
            <Card className="bg-white border-slate-200 shadow-sm overflow-hidden">
              <CardContent className="p-0 overflow-x-auto">
                <table className="w-full text-sm" data-testid="proxy-logs-table">
                  <thead>
                    <tr className="border-b border-slate-200 bg-slate-50">
                      <th className="text-left px-4 py-2.5 text-[10px] uppercase tracking-wider text-slate-600 font-bold">When</th>
                      <th className="text-left px-4 py-2.5 text-[10px] uppercase tracking-wider text-slate-600 font-bold">Model</th>
                      <th className="text-left px-4 py-2.5 text-[10px] uppercase tracking-wider text-slate-600 font-bold">Status</th>
                      <th className="text-left px-4 py-2.5 text-[10px] uppercase tracking-wider text-slate-600 font-bold">Tokens</th>
                      <th className="text-left px-4 py-2.5 text-[10px] uppercase tracking-wider text-slate-600 font-bold">Cost</th>
                      <th className="text-left px-4 py-2.5 text-[10px] uppercase tracking-wider text-slate-600 font-bold">Latency</th>
                    </tr>
                  </thead>
                  <tbody>
                    {requests.length === 0 ? (
                      <tr><td colSpan="6" className="text-center text-slate-500 py-10">No proxied requests yet. Configure a client using the setup tab above.</td></tr>
                    ) : requests.map((r) => (
                      <tr key={r.req_id} className="border-b border-slate-200 hover:bg-slate-50 cursor-pointer" onClick={() => setReqDetail(r)} data-testid={`proxy-log-row-${r.req_id}`}>
                        <td className="px-4 py-3 text-slate-500 text-[11px]">{fmtDate(r.created_at)}</td>
                        <td className="px-4 py-3 text-slate-600 font-mono text-[11px]">{r.model}</td>
                        <td className="px-4 py-3">
                          {(r.status_code || 200) >= 400 ?
                            <Badge className="bg-rose-500/15 border-rose-500/40 text-rose-300 px-1.5 py-0 text-[10px]">{r.status_code}</Badge> :
                            <Badge className="bg-emerald-500/15 border-emerald-500/30 text-emerald-300 px-1.5 py-0 text-[10px]">{r.status_code || 200}</Badge>}
                        </td>
                        <td className="px-4 py-3 text-slate-600 font-mono text-[11px]">{r.prompt_tokens}→{r.completion_tokens}</td>
                        <td className="px-4 py-3 text-emerald-300 font-mono text-[11px]">{fmtCost(r.cost_usd)}</td>
                        <td className="px-4 py-3 text-slate-600 font-mono text-[11px]">{r.latency_ms}ms</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* SAVINGS — Auto-Compiler Proxy v2 */}
          <TabsContent value="savings" className="mt-6 space-y-4">
            {savings && (savings.cache_hits > 0 || savings.cascade_count > 0 || savings.tokens_saved_compaction > 0) ? (
              <>
                {/* Hero totals */}
                <div className="grid sm:grid-cols-4 gap-3" data-testid="savings-hero">
                  <Card className="bg-emerald-500/10 border-emerald-500/30 shadow-sm">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-emerald-300 font-bold"><Sparkles className="w-3.5 h-3.5" /> Total saved</div>
                      <div className="text-2xl font-extrabold text-emerald-300 mt-1 font-mono tabular-nums" data-testid="savings-total">{fmtCost(savings.total_savings_usd || 0)}</div>
                    </CardContent>
                  </Card>
                  <Card className="bg-white border-slate-200 shadow-sm">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-sky-600 font-bold"><Database className="w-3.5 h-3.5" /> Cache hits</div>
                      <div className="text-2xl font-bold text-slate-900 mt-1 font-mono tabular-nums" data-testid="savings-cache-hits">{savings.cache_hits || 0}</div>
                      <div className="text-[11px] text-slate-500 mt-0.5">{fmtCost(savings.cache_savings_usd || 0)} saved</div>
                    </CardContent>
                  </Card>
                  <Card className="bg-white border-slate-200 shadow-sm">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-amber-600 font-bold"><GitBranch className="w-3.5 h-3.5" /> Cascade hits</div>
                      <div className="text-2xl font-bold text-slate-900 mt-1 font-mono tabular-nums" data-testid="savings-cascade-count">{savings.cascade_count || 0}</div>
                      <div className="text-[11px] text-slate-500 mt-0.5">{fmtCost(savings.cascade_savings_usd || 0)} saved</div>
                    </CardContent>
                  </Card>
                  <Card className="bg-white border-slate-200 shadow-sm">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-violet-600 font-bold"><Scissors className="w-3.5 h-3.5" /> Tokens stripped</div>
                      <div className="text-2xl font-bold text-slate-900 mt-1 font-mono tabular-nums" data-testid="savings-tokens-saved">{(savings.tokens_saved_compaction || 0).toLocaleString()}</div>
                      <div className="text-[11px] text-slate-500 mt-0.5">by the compactor</div>
                    </CardContent>
                  </Card>
                </div>

                {/* v35.08 — Streaming-aware cache */}
                {streamStats && (
                  <Card className="bg-emerald-50 bg-gradient-to-br from-emerald-50 via-white to-white border-emerald-200 shadow-sm" data-testid="stream-cache-card">
                    <CardContent className="p-5">
                      <div className="flex items-start justify-between gap-3 flex-wrap">
                        <div>
                          <div className="flex items-center gap-2">
                            <Zap className="w-4 h-4 text-emerald-500" />
                            <h3 className="text-sm font-bold text-slate-900">Streaming-aware cache</h3>
                            <Badge className="bg-emerald-500/15 border-emerald-500/30 text-emerald-700 text-[9px]">NEW</Badge>
                          </div>
                          <p className="text-[12px] text-slate-600 mt-1 max-w-xl">
                            Captures SSE chunks on first call, replays them on identical-prompt hits.
                            Unlocks the <strong>80% of prod traffic</strong> v2 cache was skipping.
                          </p>
                        </div>
                      </div>
                      <div className="mt-4 grid sm:grid-cols-4 gap-3">
                        <div className="rounded-lg bg-white border border-slate-200 p-3">
                          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-mono">Entries</div>
                          <div className="text-xl font-bold text-slate-900 mt-1 font-mono tabular-nums" data-testid="stream-cache-entries">{streamStats.entries || 0}</div>
                        </div>
                        <div className="rounded-lg bg-white border border-slate-200 p-3">
                          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-mono">Hits</div>
                          <div className="text-xl font-bold text-slate-900 mt-1 font-mono tabular-nums" data-testid="stream-cache-hits">{streamStats.hits || 0}</div>
                        </div>
                        <div className="rounded-lg bg-white border border-slate-200 p-3">
                          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-mono">$ saved</div>
                          <div className="text-xl font-bold text-emerald-600 mt-1 font-mono tabular-nums" data-testid="stream-cache-saved">{fmtCost(streamStats.saved_usd || 0)}</div>
                        </div>
                        <div className="rounded-lg bg-white border border-slate-200 p-3">
                          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-mono">Chunks stored</div>
                          <div className="text-xl font-bold text-slate-900 mt-1 font-mono tabular-nums" data-testid="stream-cache-chunks">{(streamStats.total_chunks_stored || 0).toLocaleString()}</div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* v35.14 — RAG Embedding Cache */}
                {embeddingStats && (
                  <Card className="bg-violet-50 bg-gradient-to-br from-violet-50 via-white to-white border-violet-200 shadow-sm" data-testid="embedding-cache-card">
                    <CardContent className="p-5">
                      <div className="flex items-start justify-between gap-3 flex-wrap">
                        <div>
                          <div className="flex items-center gap-2">
                            <Database className="w-4 h-4 text-violet-500" />
                            <h3 className="text-sm font-bold text-slate-900">RAG embedding cache</h3>
                            <Badge className="bg-violet-500/15 border-violet-500/30 text-violet-700 text-[9px]">NEW</Badge>
                          </div>
                          <p className="text-[12px] text-slate-600 mt-1 max-w-xl">
                            Caches duplicate document embeddings (OpenAI / Cohere / Voyage / Mistral).
                            Closes the <strong>30-40% byte-identical re-embedding leak</strong> typical of RAG apps.
                            7-day TTL · per-tenant scoped.
                          </p>
                        </div>
                        <Link
                          to="/how-it-saves#embedding-cache"
                          className="shrink-0 text-[11px] font-mono text-violet-600 hover:text-violet-700 underline underline-offset-2"
                          data-testid="embedding-cache-explain-link"
                        >
                          How it saves →
                        </Link>
                      </div>
                      <div className="mt-4 grid sm:grid-cols-4 gap-3">
                        <div className="rounded-lg bg-white border border-slate-200 p-3">
                          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-mono">Vectors cached</div>
                          <div className="text-xl font-bold text-slate-900 mt-1 font-mono tabular-nums" data-testid="embedding-cache-entries">{(embeddingStats.entries || 0).toLocaleString()}</div>
                        </div>
                        <div className="rounded-lg bg-white border border-slate-200 p-3">
                          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-mono">Cache hits</div>
                          <div className="text-xl font-bold text-slate-900 mt-1 font-mono tabular-nums" data-testid="embedding-cache-hits">{(embeddingStats.hits || 0).toLocaleString()}</div>
                        </div>
                        <div className="rounded-lg bg-white border border-slate-200 p-3">
                          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-mono">$ saved</div>
                          <div className="text-xl font-bold text-violet-600 mt-1 font-mono tabular-nums" data-testid="embedding-cache-saved">{fmtCost(embeddingStats.saved_usd || 0)}</div>
                        </div>
                        <div className="rounded-lg bg-white border border-slate-200 p-3">
                          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-mono">Approx. hit rate</div>
                          <div className="text-xl font-bold text-slate-900 mt-1 font-mono tabular-nums" data-testid="embedding-cache-hit-rate">{(embeddingStats.approximate_hit_rate_pct || 0).toFixed(1)}%</div>
                        </div>
                      </div>
                      {(embeddingStats.by_model || []).length > 0 && (
                        <div className="mt-4 border-t border-slate-100 pt-3">
                          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-mono mb-2">By model · top {(embeddingStats.by_model || []).length}</div>
                          <div className="space-y-1.5" data-testid="embedding-cache-by-model">
                            {(embeddingStats.by_model || []).slice(0, 5).map((m, i) => (
                              <div key={i} className="flex items-center justify-between text-xs">
                                <span className="font-mono text-slate-700 truncate flex-1">{m.model}</span>
                                <span className="font-mono text-slate-500 px-2 shrink-0">{(m.entries || 0).toLocaleString()} cached</span>
                                <span className="font-mono text-violet-600 font-semibold shrink-0">{fmtCost(m.saved_usd || 0)}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )}

                {/* Recent events */}
                {(savings.recent || []).length > 0 && (
                  <Card className="bg-white border-slate-200 shadow-sm">
                    <CardContent className="p-5">
                      <h3 className="text-sm font-bold text-slate-900 mb-3 flex items-center gap-2"><Activity className="w-4 h-4 text-emerald-400" /> Recent savings events</h3>
                      <div className="space-y-1.5" data-testid="savings-recent-list">
                        {(savings.recent || []).slice(0, 15).map((r, i) => (
                          <div key={i} className="flex items-center justify-between text-xs border-b border-slate-100 last:border-0 pb-1.5" data-testid={`savings-event-${i}`}>
                            <div className="flex items-center gap-2 min-w-0 flex-1">
                              <span className="text-[10px] text-slate-500 font-mono shrink-0">{fmtDate(r.ts)}</span>
                              <span className="font-mono text-slate-700 truncate">{r.model}</span>
                              {r.cache_hit && <Badge className="bg-sky-500/15 border-sky-500/30 text-sky-700 px-1.5 py-0 text-[9px]">cache</Badge>}
                              {r.cascade_used && <Badge className="bg-amber-500/15 border-amber-500/30 text-amber-700 px-1.5 py-0 text-[9px]">cascade</Badge>}
                              {r.tokens_saved > 0 && <Badge className="bg-violet-500/15 border-violet-500/30 text-violet-700 px-1.5 py-0 text-[9px]">−{r.tokens_saved} tok</Badge>}
                            </div>
                            <span className="text-emerald-600 font-mono font-bold text-xs">{fmtCost(r.saved_usd)}</span>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </>
            ) : (
              <Card className="bg-white border-slate-200 shadow-sm border-dashed" data-testid="savings-empty-state">
                <CardContent className="p-12 text-center text-slate-500">
                  <Sparkles className="w-10 h-10 mx-auto mb-3 text-emerald-400" />
                  <p className="font-bold text-slate-900 mb-1">The Auto-Compiler is armed and waiting.</p>
                  <p className="text-sm max-w-md mx-auto leading-relaxed">
                    Send a request through your proxy key — compaction, semantic cache,
                    and optional cascade routing will start saving you money on every call.
                  </p>
                  <div className="mt-4 flex flex-wrap gap-2 justify-center text-[11px]">
                    <code className="bg-slate-950 text-emerald-300 px-2 py-1 rounded">X-Cartie-Compile: true</code>
                    <code className="bg-slate-950 text-emerald-300 px-2 py-1 rounded">X-Cartie-Cache: true</code>
                    <code className="bg-slate-950 text-emerald-300 px-2 py-1 rounded">X-Cartie-Cascade: true</code>
                  </div>
                  <p className="text-[11px] text-slate-400 mt-3">
                    Compile + Cache are <span className="font-bold">ON by default</span>. Cascade is opt-in.
                  </p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* STATS */}
          <TabsContent value="stats" className="mt-6 space-y-4">
            {stats && stats.request_count > 0 ? (
              <>
                <div className="grid sm:grid-cols-4 gap-3">
                  {[
                    { label: 'Requests', value: stats.request_count, icon: <Activity className="w-3.5 h-3.5" /> },
                    { label: 'Total cost', value: fmtCost(stats.total_cost_usd), icon: <DollarSign className="w-3.5 h-3.5" /> },
                    { label: 'p95 latency', value: `${stats.p95_latency_ms}ms`, icon: <Zap className="w-3.5 h-3.5" /> },
                    { label: 'Error rate', value: `${(stats.error_rate * 100).toFixed(1)}%`, icon: <AlertCircle className="w-3.5 h-3.5" /> },
                  ].map((s, i) => (
                    <Card key={i} className="bg-white border-slate-200 shadow-sm">
                      <CardContent className="p-4">
                        <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-slate-500 font-bold">{s.icon}{s.label}</div>
                        <div className="text-xl font-bold text-emerald-300 mt-1 font-mono">{s.value}</div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
                <Card className="bg-white border-slate-200 shadow-sm">
                  <CardContent className="p-5">
                    <h3 className="text-sm font-bold text-slate-900 mb-3 flex items-center gap-2"><Cpu className="w-4 h-4 text-emerald-400" /> By model</h3>
                    <div className="space-y-1.5">
                      {(stats.by_model || []).map((b) => {
                        const maxCost = Math.max(...(stats.by_model || []).map((x) => x.cost_usd), 0.000001);
                        const pct = Math.round((b.cost_usd / maxCost) * 100);
                        return (
                          <div key={b.model} className="grid grid-cols-[180px_1fr_120px] items-center gap-3 text-xs" data-testid={`proxy-stats-model-${b.model}`}>
                            <span className="font-mono text-slate-700 truncate">{b.model}</span>
                            <div className="h-2 bg-slate-950 text-slate-100 rounded overflow-hidden"><div className="h-full bg-emerald-500/50" style={{ width: `${pct}%` }} /></div>
                            <span className="text-emerald-300 font-mono text-right">{fmtCost(b.cost_usd)} · {b.count}×</span>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              </>
            ) : (
              <Card className="bg-white border-slate-200 shadow-sm border-dashed">
                <CardContent className="p-12 text-center text-slate-500">
                  <Activity className="w-10 h-10 mx-auto mb-3 text-slate-600" />
                  <p>No requests yet — configure a client using the Setup tab and start sending traffic.</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>

        {/* Request detail */}
        <Dialog open={!!reqDetail} onOpenChange={(o) => !o && setReqDetail(null)}>
          <DialogContent className="bg-slate-900 border-slate-800 text-slate-100 max-w-3xl">
            <DialogHeader><DialogTitle>Request · {reqDetail?.req_id}</DialogTitle></DialogHeader>
            {reqDetail && (
              <div className="space-y-3 text-xs">
                <div className="grid grid-cols-4 gap-2">
                  <Card className="bg-white border-slate-300"><CardContent className="p-2.5"><div className="text-[10px] text-slate-500 uppercase">Model</div><div className="font-mono text-slate-200">{reqDetail.model}</div></CardContent></Card>
                  <Card className="bg-white border-slate-300"><CardContent className="p-2.5"><div className="text-[10px] text-slate-500 uppercase">Tokens</div><div className="font-mono text-slate-200">{reqDetail.prompt_tokens}→{reqDetail.completion_tokens}</div></CardContent></Card>
                  <Card className="bg-white border-slate-300"><CardContent className="p-2.5"><div className="text-[10px] text-slate-500 uppercase">Cost</div><div className="font-mono text-emerald-300">{fmtCost(reqDetail.cost_usd)}</div></CardContent></Card>
                  <Card className="bg-white border-slate-300"><CardContent className="p-2.5"><div className="text-[10px] text-slate-500 uppercase">Latency</div><div className="font-mono text-slate-200 flex items-center gap-1"><Clock className="w-3 h-3" />{reqDetail.latency_ms}ms</div></CardContent></Card>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1">Request</div>
                  <pre className="bg-slate-950 p-2 rounded border border-slate-800 max-h-40 overflow-auto whitespace-pre-wrap text-slate-300 font-mono">{reqDetail.request_summary}</pre>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1">Response</div>
                  <pre className="bg-slate-950 p-2 rounded border border-slate-800 max-h-40 overflow-auto whitespace-pre-wrap text-emerald-200 font-mono">{reqDetail.response_summary}</pre>
                </div>
                {reqDetail.error && <div className="text-rose-300 font-mono">{reqDetail.error}</div>}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
};

export default LlmProxy;
